import { Construction, ExternalLink, Megaphone, Smartphone } from 'lucide-react';
import { InstallButton } from '../../components/InstallButton.tsx';
import { Link } from 'react-router';
import { DATA_SOURCES, type DataSource } from '../../../shared/domain/index.ts';
import { COMMUNITY_NOTICE, INDEPENDENCE_NOTICE } from '../../../shared/seo/pages.ts';
import { PageContainer } from '../../components/layout/PageContainer.tsx';

const OFFICIAL_LINKS = {
  city: 'https://www.ermont.fr',
  cityApp: 'https://www.ermont.fr/2252-4411/fiche/ermont-l-appli-est-disponible.htm',
  cityWorks: 'https://www.ermont.fr/2267/en-1-clic/travaux-et-chantiers-de-la-ville.htm',
  agglomerationWorks: 'https://www.valparisis.fr/agglomeration/en-ce-moment/travaux-en-cours',
} as const;

const SOURCES: readonly DataSource[] = Object.values(DATA_SOURCES);

function External({ href, children }: { href: string; children: string }) {
  return (
    <a href={href} rel="noopener noreferrer" target="_blank">
      {children} <ExternalLink size={14} aria-hidden="true" />
      <span className="visually-hidden"> (nouvel onglet)</span>
    </a>
  );
}

export function RoadworksPage() {
  return (
    <PageContainer path="/travaux">
      <section className="card" aria-labelledby="works-title">
        <h2 className="card__title" id="works-title">
          <Construction size={20} aria-hidden="true" /> Bientôt disponible
        </h2>
        <p>
          Aucune source de données structurée sur les travaux à Ermont n’est disponible à ce jour. Plutôt que d’afficher
          des informations incomplètes, nous renvoyons vers les pages officielles :
        </p>
        <ul>
          <li>
            <External href={OFFICIAL_LINKS.cityWorks}>Travaux et chantiers (Ville d’Ermont)</External>
          </li>
          <li>
            <External href={OFFICIAL_LINKS.agglomerationWorks}>Travaux en cours (Val Parisis)</External>
          </li>
        </ul>
      </section>
    </PageContainer>
  );
}

export function AboutPage() {
  return (
    <PageContainer path="/a-propos">
      <section className="card" aria-labelledby="about-independent">
        <h2 id="about-independent">Un projet indépendant</h2>
        <p>
          <strong>{INDEPENDENCE_NOTICE}</strong> Il agrège des données publiques pour répondre à une question simple :
          que se passe-t-il à Ermont (Val-d’Oise, 95, Île-de-France) maintenant ?
        </p>
        <p>
          Chaque information affiche sa source et l’heure de sa dernière mise à jour. Une donnée ancienne est signalée
          comme telle ; une donnée indisponible n’est jamais remplacée par une valeur inventée.
        </p>
      </section>

      <section className="card" aria-labelledby="about-install">
        <h2 id="about-install" className="card__title">
          <Smartphone size={20} aria-hidden="true" /> Installer Ermont Live sur votre téléphone
        </h2>
        <p>
          Ermont Live s’installe comme une application : une icône sur l’écran d’accueil, un démarrage direct, et les
          dernières données consultées restent lisibles sans réseau. Aucun magasin d’applications, aucun compte.
        </p>
        <InstallButton variant="text" />
        <ul>
          <li>
            <strong>Android (Chrome)</strong> : le bouton ci-dessus, ou le menu du navigateur puis « Installer
            l’application ».
          </li>
          <li>
            <strong>iPhone et iPad (Safari)</strong> : le bouton Partager, puis « Sur l’écran d’accueil ».
          </li>
          <li>
            <strong>Ordinateur (Chrome, Edge)</strong> : l’icône d’installation dans la barre d’adresse.
          </li>
        </ul>
      </section>

      <section className="card" aria-labelledby="about-report">
        <h2 id="about-report" className="card__title">
          <Megaphone size={20} aria-hidden="true" /> Signaler un problème dans la ville
        </h2>
        <p>
          Pour un problème de voirie, d’éclairage, de propreté…, adressez-vous aux services compétents, par exemple via{' '}
          <External href={OFFICIAL_LINKS.cityApp}>l’application officielle de la Ville d’Ermont</External> ou le{' '}
          <External href={OFFICIAL_LINKS.city}>site de la ville</External>.
        </p>
        <p>
          <em>{COMMUNITY_NOTICE}</em>
        </p>
      </section>

      <section className="card" aria-labelledby="about-sources">
        <h2 id="about-sources">Sources et licences</h2>
        <ul className="item-list">
          {SOURCES.map((source) => (
            <li key={source.id}>
              <strong>{source.name}</strong>
              <span className="item-list__meta">
                Licence :{' '}
                {source.license.url === undefined ? (
                  source.license.name
                ) : (
                  <External href={source.license.url}>{source.license.name}</External>
                )}
                {' · '}
                {source.attribution}
              </span>
            </li>
          ))}
          <li>
            <strong>Fond de carte</strong>
            <span className="item-list__meta">
              <External href="https://openfreemap.org">OpenFreeMap</External> ·{' '}
              <External href="https://openmaptiles.org">© OpenMapTiles</External> · Données{' '}
              <External href="https://www.openstreetmap.org/copyright">© les contributeurs d’OpenStreetMap</External>
            </span>
          </li>
        </ul>
      </section>

      <section className="card" aria-labelledby="about-privacy">
        <h2 id="about-privacy">Confidentialité</h2>
        <p>
          Pas de compte, pas de cookie publicitaire, pas de traceur. Vos préférences (gare favorite, calques de la
          carte) restent dans votre navigateur. Votre position n’est jamais envoyée à nos serveurs. Le fond de carte est
          chargé depuis OpenFreeMap, qui reçoit donc votre adresse IP et la zone affichée.
        </p>
        <p>
          <Link to="/">Retour à l’accueil</Link>
        </p>
      </section>
    </PageContainer>
  );
}

export function NotFoundPage() {
  return (
    <section className="container page">
      <title>Page introuvable | Ermont Live</title>
      <meta name="robots" content="noindex" />
      <h1 className="page-title">Page introuvable</h1>
      <p className="page-intro">Cette adresse ne correspond à aucune page d’Ermont Live.</p>
      <p>
        <Link className="button button--primary" to="/">
          Revenir à l’accueil
        </Link>
      </p>
    </section>
  );
}
